import React, { useCallback, useContext, useMemo, useState, useEffect } from 'react';
import { TextField, Stack, Typography, Button } from '@mui/material';

import { translate as t } from 'hooks/useTranslations';

import Modal from 'components/_layouts/Modal';
import { AppContext } from 'views/App/context';
import useReviewWorkflows from 'views/App/Portal/PerformanceReporting/ReviewWorkflows/_data_sources/useReviewWorkflows';
import type { TemplateReviewWorkflow } from '../../_types';
import ReviewWorkflowPicker from '../../_components/ReviewWorkflowPicker';

interface PublishTemplateDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (payload: { changeNote: string; reviewWorkflowId: number }) => Promise<void> | void;
  isSaving: boolean;
  existingReviewWorkflows?: TemplateReviewWorkflow[];
}

const PublishTemplateDialog: React.FC<PublishTemplateDialogProps> = ({
  open,
  onClose,
  onConfirm,
  isSaving,
  existingReviewWorkflows,
}) => {
  const { currentOrganizationId } = useContext(AppContext);
  const [selectedReviewWorkflowId, setSelectedReviewWorkflowId] = useState<number | ''>('');
  const [changeNote, setChangeNote] = useState('');
  const [publishTouched, setPublishTouched] = useState(false);

  const queryParams = useMemo(() => {
    return {
      organizationId: currentOrganizationId,
    };
  }, [currentOrganizationId]);
  const reviewWorkflowsQuery = useReviewWorkflows(queryParams);
  const reviewWorkflows = reviewWorkflowsQuery.data?.pages.flatMap((page) => page.content) ?? [];
  const { isLoading: isReviewWorkflowsLoading } = reviewWorkflowsQuery;

  const reviewWorkflowOptions = useMemo(() => {
    const fromExisting = (existingReviewWorkflows ?? []).map((w) => ({ value: w.id, label: w.name }));
    const fromApi = reviewWorkflows.map((w) => ({ value: w.reviewWorkflowId, label: w.name }));
    const byValue = new Map<number, { value: number; label: string }>();
    [...fromExisting, ...fromApi].forEach((opt) => byValue.set(opt.value, opt));
    return Array.from(byValue.values());
  }, [existingReviewWorkflows, reviewWorkflows]);

  const resetState = useCallback(() => {
    setSelectedReviewWorkflowId('');
    setChangeNote('');
    setPublishTouched(false);
  }, []);

  useEffect(() => {
    if (!open) {
      resetState();
    } else {
      const activeId = existingReviewWorkflows?.find((w) => w.isActive)?.id;
      if (activeId != null) {
        setSelectedReviewWorkflowId(activeId);
      }
    }
  }, [open, resetState, existingReviewWorkflows]);

  const reviewWorkflowError = publishTouched && !selectedReviewWorkflowId;
  const canPublish = Boolean(selectedReviewWorkflowId);

  const reviewWorkflowHelperText = useMemo(() => {
    if (reviewWorkflowError) {
      return t('performanceReporting.templates.builder.publishModal.reviewWorkflowRequired');
    }

    if (isReviewWorkflowsLoading) {
      return t('performanceReporting.templates.builder.publishModal.reviewWorkflowLoading');
    }

    if (!reviewWorkflowOptions.length) {
      return t('performanceReporting.templates.builder.publishModal.reviewWorkflowEmpty');
    }

    return t('performanceReporting.templates.builder.publishModal.reviewWorkflowHelper');
  }, [isReviewWorkflowsLoading, reviewWorkflowError, reviewWorkflows.length]);

  const changeNoteHelperText = t('performanceReporting.templates.builder.publishModal.changeNoteHelper');

  const handleConfirm = useCallback(async () => {
    const trimmedChangeNote = changeNote.trim();
    setPublishTouched(true);

    if (!selectedReviewWorkflowId) {
      return;
    }

    await onConfirm({
      changeNote: trimmedChangeNote,
      reviewWorkflowId: Number(selectedReviewWorkflowId),
    });
  }, [changeNote, onConfirm, selectedReviewWorkflowId]);

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={t('performanceReporting.templates.builder.publishModal.title')}
      actions={
        <>
          <Button
            variant='outlined'
            color='primary'
            onClick={onClose}
            disabled={isSaving}
          >
            {t('performanceReporting.templates.builder.publishModal.keepEditing')}
          </Button>
          <Button
            variant='contained'
            color='primary'
            onClick={handleConfirm}
            disabled={!canPublish || isSaving || isReviewWorkflowsLoading || !reviewWorkflowOptions.length}
            loading={isSaving}
            loadingPosition='end'
          >
            {t('performanceReporting.templates.builder.publishModal.publish')}
          </Button>
        </>
      }
    >
      <Stack
        spacing={3}
        sx={{ mt: 1 }}
      >
        <Typography
          variant='body2'
          color='text.secondary'
        >
          {t('performanceReporting.templates.builder.publishModal.description')}
        </Typography>
        <ReviewWorkflowPicker
          id='publish-review-workflow'
          options={reviewWorkflowOptions}
          isLoading={isReviewWorkflowsLoading}
          value={selectedReviewWorkflowId}
          onChange={(value) => setSelectedReviewWorkflowId(value ?? '')}
          label={t('performanceReporting.templates.builder.publishModal.reviewWorkflowLabel')}
          placeholder={t('performanceReporting.templates.builder.publishModal.reviewWorkflowPlaceholder')}
          required
          error={reviewWorkflowError}
          helperText={reviewWorkflowHelperText}
          disabled={isReviewWorkflowsLoading || !reviewWorkflowOptions.length}
        />
        <TextField
          label={t('performanceReporting.templates.builder.publishModal.changeNoteLabel')}
          value={changeNote}
          onChange={(event) => setChangeNote(event.target.value)}
          fullWidth
          multiline
          minRows={3}
          helperText={changeNoteHelperText}
        />
      </Stack>
    </Modal>
  );
};

export default PublishTemplateDialog;
